import type { IUserPreferences } from './user-preferences.types';
export type UserRole = 'superadmin' | 'admin' | 'editor' | 'readonly';
export type QuoteRole = 'QUOTE_MANAGER' | 'QUOTE_SALES' | null;

/**
 * Modules optionnels activables par société depuis le superadmin.
 * Le socle (cotation grille, imports, règles, transporteurs, réglages…)
 * reste toujours actif et n'apparaît pas ici.
 */
export type FeatureKey =
  | 'quote'
  | 'quoteResponse'
  | 'operations'
  | 'sourcing'
  | 'tenders'
  | 'carrierPool'
  | 'autoTenders'
  | 'robert'
  | 'budget'
  | 'budgetAudit'
  | 'budgetOptimal';

export type ICompanyFeatures = Record<FeatureKey, boolean>;

/**
 * Défauts appliqués aux nouvelles sociétés ET au backfill des sociétés
 * existantes. Règle : un module *nouveau* (jamais vu en prod) démarre à
 * `false` (caché, vendu en option) ; un module *déjà visible* en prod
 * démarre à `true` pour ne pas le retirer aux clients actuels.
 */
export const FEATURE_DEFAULTS: ICompanyFeatures = {
  quote: true, // Devis : déjà visible en prod
  quoteResponse: false, // nouveau → caché, vendu en option (page « Répondre à une cotation », sous le Devis)
  operations: false, // nouveau → caché, vendu en option
  sourcing: false, // était couplé à `tenders` (masqué en prod)
  tenders: false, // déjà masqué par flag en prod
  carrierPool: false, // était couplé à `tenders` (masqué en prod)
  autoTenders: false, // nouveau / beta
  robert: false, // déjà masqué par flag en prod
  budget: true, // déjà visible en prod (onglet Réalisé)
  budgetAudit: true, // onglet Audit : était couplé à `budget`
  budgetOptimal: true, // onglet Optimal : était couplé à `budget`
};

/** Métadonnées pour l'UI superadmin (ordre = ordre d'affichage). */
export const FEATURE_REGISTRY: { key: FeatureKey; label: string; hint: string }[] = [
  { key: 'quote', label: 'Devis unitaire', hint: 'Cotation sur grille' },
  { key: 'quoteResponse', label: 'Devis en masse', hint: 'Réponse à une demande de tarifs depuis les grilles' },
  { key: 'operations', label: 'Opérations', hint: 'Console affrètement, commandes, cotation spot' },
  { key: 'sourcing', label: 'Sourcing', hint: 'Recherche de transporteurs' },
  { key: 'tenders', label: "Appels d'offres", hint: 'Consultations transporteurs' },
  { key: 'carrierPool', label: 'Vivier de transporteurs', hint: 'Transporteurs invités et prospects' },
  { key: 'autoTenders', label: 'AO Automotive (FVL)', hint: 'Réseau multi-legs / commissionnaire' },
  { key: 'robert', label: 'Robert', hint: 'Agent expert IA' },
  { key: 'budget', label: 'Budget · Réalisé', hint: 'Coûts réels, datasets, scénarios' },
  { key: 'budgetAudit', label: 'Budget · Audit', hint: 'Simulations & écarts' },
  { key: 'budgetOptimal', label: 'Budget · Optimal', hint: 'Choix transporteurs' },
];

/**
 * Restrictions de modules propres à une personne (`users.module_access`).
 *
 * Un module absent vaut « autorisé » : par défaut chacun voit tout ce que sa
 * société a activé. Seul `false` retire un module à la personne. Un
 * administrateur n'est jamais restreint — il reste le super-admin de sa
 * société — et un module que la société n'a pas ne peut pas être rendu par
 * ce biais.
 */
export type IUserModuleAccess = Partial<Record<FeatureKey, boolean>>;

/**
 * Le module est-il ouvert à cette personne ? Source de vérité unique, partagée
 * par l'API (FeatureGuard) et l'écran (nav, routes, palette).
 */
export function isFeatureEnabledForUser(
  user: {
    role: string;
    moduleAccess?: IUserModuleAccess | null;
    company?: { features?: Partial<Record<FeatureKey, boolean>> | null } | null;
  },
  key: FeatureKey,
): boolean {
  if (user.role === 'superadmin') return true;
  const companyHas = user.company?.features?.[key] ?? FEATURE_DEFAULTS[key];
  if (!companyHas) return false;
  if (user.role === 'admin') return true;
  return user.moduleAccess?.[key] !== false;
}

/** Ne garde que les clés de module connues, en booléens. */
export function sanitizeModuleAccess(input: unknown): IUserModuleAccess {
  const out: IUserModuleAccess = {};
  if (!input || typeof input !== 'object') return out;
  for (const { key } of FEATURE_REGISTRY) {
    const v = (input as Record<string, unknown>)[key];
    if (typeof v === 'boolean') out[key] = v;
  }
  return out;
}

export interface ICompany {
  id: string;
  name: string;
  country: string;
  features: ICompanyFeatures;
  /** Organisation du portail Pool4ward liée à cette société (SSO) ; vide = non basculée. */
  portalOrganizationId?: string | null;
  createdAt: string | Date;
  updatedAt: string | Date;
}

export interface IUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  /** Profil actif ; `false` = désactivé par un superadmin (connexion refusée). */
  isActive?: boolean;
  canQuote: boolean;
  quoteRole: QuoteRole;
  /** Modules retirés à cette personne (cf. `IUserModuleAccess`). */
  moduleAccess?: IUserModuleAccess;
  companyId: string | null;
  company?: ICompany | null;
  /** Réglages d'écran propres à cette personne (cf. `IUserPreferences`). */
  preferences?: IUserPreferences;
  /** Compte du portail Pool4ward lié à cette personne (SSO) ; vide = pas de SSO. */
  portalUserId?: string | null;
  createdAt: string | Date;
  updatedAt: string | Date;
}

/** Une organisation (société) dans laquelle la personne qui se connecte peut entrer. */
export interface ILoginCompanyChoice {
  id: string;
  name: string;
}

/**
 * Réponse de `POST /auth/login` quand le mot de passe est bon mais que la personne
 * a un compte dans plusieurs sociétés : aucune session n'est ouverte, l'écran
 * propose les organisations et renvoie la même requête avec `companyId`.
 */
export interface ILoginCompanySelection {
  requiresCompanySelection: true;
  companies: ILoginCompanyChoice[];
}

export function isLoginCompanySelection(result: unknown): result is ILoginCompanySelection {
  return (
    typeof result === 'object' &&
    result !== null &&
    (result as ILoginCompanySelection).requiresCompanySelection === true
  );
}

export interface ITenantBranding {
  companyId: string;
  logoUrl: string | null;
  brandName: string | null;
  accentColor: string;
  accentHoverColor: string | null;
  accentSoftColor: string | null;
  updatedAt: string | Date;
}

export type EmailTemplateKind = 'invitation' | 'password_reset';

export interface IEmailTemplate {
  companyId: string;
  kind: EmailTemplateKind;
  subject: string | null;
  heading: string | null;
  body: string | null;
  buttonLabel: string | null;
  footer: string | null;
  updatedAt: string | Date;
}

export type MarginType = 'PERCENT' | 'FIXED';

export interface ICarrierMarginRule {
  id: string;
  companyId: string;
  carrierId: string | null;
  bandId: string | null;
  marginType: MarginType;
  marginValue: number;
  active: boolean;
  createdAt: string | Date;
  updatedAt: string | Date;
}

export interface IInvitation {
  id: string;
  email: string;
  role: UserRole;
  companyId: string;
  company?: ICompany;
  invitedBy: string | null;
  status: 'pending' | 'accepted' | 'expired';
  expiresAt: string | Date;
  createdAt: string | Date;
}
